import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Career } from 'app/shared/model/career.model'; // Import the Career interface
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { ToastContext } from 'app/context/ToastContext';

const validationSchema = Yup.object({
  position: Yup.string().required('Pozícia je povinný údaj').max(255, 'Max 255 znakov.'),
  positionValue: Yup.number().required('Hodnota je povinný údaj').min(0, 'Hodnota musí byť číslo väčšie ako 0'),
  percents: Yup.number().required('Percentá sú povinné').min(0, 'Percentá musia byť číslo väčšie ako 0'),
  promotionCondition: Yup.string().max(255, 'Max 255 znakov'),
});

const EditCareer: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext); // Use the context for toast

  const [career, setCareer] = useState<Career>({
    id: parseInt(id, 10),
    careerType: 0,
    position: '',
    positionValue: 0,
    percents: 0,
    promotionCondition: '',
  });

  useEffect(() => {
    // Fetch the specific career data by ID
    axios
      .get<Career>(`/api/careers/${id}`)
      .then(response => {
        setCareer(response.data);
      })
      .catch(error => {
        showError('Chyba', 'Chyba pri nacitavani kariery.');
        console.error('Error fetching career data:', error);
      });
  }, [id]);

  // Handle form submit to update careerType using `edit-career-type` endpoint
  const saveCareer = (values: Career) => {
    axios
      .put(`/api/careers/${career.id}`, {
        id: career.id,
        careerType: career.careerType,
        position: career.position,
        positionValue: values.positionValue,
        percents: values.percents,
        promotionCondition: values.promotionCondition,
      })
      .then(response => {
        showSuccess('Úspech', 'Kariéra úspešne zmenená.');
        navigate(`/careers/${career.careerType}?p=${tempParameters}`);
      })
      .catch(error => {
        showError('Chyba', 'Neočakávaná chyba.');
        console.error('Error updating career data:', error);
      });
  };

  const goBack = () => {
    navigate(`/careers/${career.careerType}?p=${tempParameters}`); // Go back to the list of careers for the same careerType
  };

  return (
    <div className="edit-career">
      <h2>Editovať kariéru</h2>
      <Formik
        initialValues={career}
        enableReinitialize={true} // Ensures form updates with initial values from API
        validationSchema={validationSchema}
        onSubmit={values => {
          saveCareer(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
            {/* Position Field */}
            <div className="p-field">
              <label htmlFor="positionNr">Poradie</label>
              <Field disabled={true} name="positionNr" type="text" className="p-inputtext p-component" />
            </div>

            {/* Position Field */}
            <div className="p-field">
              <label htmlFor="position">Pozícia</label>
              <Field name="position" type="text" className="p-inputtext p-component" />
              <div className="error-message">
                <ErrorMessage name="position" component="div" />
              </div>
            </div>

            {/* Position Value Field */}
            <div className="p-field">
              <label htmlFor="positionValue">Hodnota pozície (EUR)</label>
              <Field name="positionValue" type="number" className="p-inputtext p-component" />
              <div className="error-message">
                <ErrorMessage name="positionValue" component="div" />
              </div>
            </div>

            {/* Percents Field */}
            <div className="p-field">
              <label htmlFor="percents">Percentá</label>
              <Field name="percents" type="number" className="p-inputtext p-component" />
              <div className="error-message">
                <ErrorMessage name="percents" component="div" />
              </div>
            </div>

            {/* Promotion Condition Field */}
            <div className="p-field">
              <label htmlFor="promotionCondition">Podmienky postupu</label>
              <Field name="promotionCondition" type="text" className="p-inputtext p-component" />
              <div className="error-message">
                <ErrorMessage name="promotionCondition" component="div" />
              </div>
            </div>

            {/* Button Row */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
              <Button
                style={{ backgroundColor: 'gray' }}
                type="button"
                label="Späť"
                icon="pi pi-arrow-left"
                className="p-button-secondary"
                onClick={goBack}
              />
              <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditCareer;
